import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChildSelectorBar } from '../components/ChildSelectorBar';
import { TrendingUp, ArrowUpRight, ArrowDownRight, ArrowRight, Star, Download, Trophy, Flame, Sparkles } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { DjayAvatar } from '../../../mascot';
import { MasteryRing } from '../../shared/MasteryRing';
import { getModuleIcon } from '../../../content';
import { getMasteryLabel } from '../../../utils/formatters';
import { EmptyState } from '../../../components/shared/EmptyState';
import { AchievementsSection } from '../../../components/progress/AchievementsSection';
import { getProgressToNextLevel } from '../../../config/constants';
import { getLevelTitle } from '../../../utils/motivationalMessages';
import { type LearnerAccount } from '../../../accounts';
import { AppState } from '../../../app/types';
import { getBenchmarkContext } from '../shared/benchmarkUtils';
import { downloadCsv } from '../../../utils/csvExport';
import { getAssessmentHistory, getReport } from '../../../assessment';
import { levelCodeToCitoLevel } from '../../../utils/citoLevelUtils';
import type { AssessmentReport } from '../../../assessment';
import { useGrowthData } from '../hooks/useGrowthData';
interface VoortgangPageProps {
  linkedLearners: LearnerAccount[];
  selectedChild: LearnerAccount | undefined;
  childData: AppState | null;
  selectedGroup: string;
  getLearnerModuleMastery: (learnerId: string, moduleId: string) => number;
  setViewingLearner: (id: string) => void;
}
type TabId = 'per-vak' | 'prestaties';
export function VoortgangPage({
  linkedLearners,
  selectedChild,
  childData,
  selectedGroup,
  getLearnerModuleMastery,
  setViewingLearner
}: VoortgangPageProps) {
  const [activeTab, setActiveTab] = useState<TabId>('per-vak');
  const [latestAssessmentReport, setLatestAssessmentReport] = useState<AssessmentReport | null>(null);
  const groupNum = parseInt(selectedGroup) || 6;
  // Load latest assessment report (RT-01: added stale-closure guard)
  useEffect(() => {
    let cancelled = false;
    if (!selectedChild) {
      setLatestAssessmentReport(null);
      return;
    }
    getAssessmentHistory(groupNum).then(history => {
      if (cancelled) return;
      const childHistory = history.filter(entry => entry.learnerId === selectedChild.id);
      if (childHistory.length > 0) {
        // Sort by completedAt descending
        childHistory.sort((a, b) => b.completedAt - a.completedAt);
        const latestAttemptId = childHistory[0].attemptId;
        getReport(latestAttemptId).then(report => {
          if (cancelled) return;
          setLatestAssessmentReport(report || null);
        }).catch(() => {});
      } else {
        setLatestAssessmentReport(null);
      }
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [selectedChild, groupNum]);
  // Shared growth computation (deduplicated from GrowthPage)
  const growthData = useGrowthData(selectedChild, childData, selectedGroup, getLearnerModuleMastery);
  // Prestaties tab data
  const prestatiesData = useMemo(() => {
    if (!childData) return {
      level: 1,
      xp: 0,
      progress: {
        current: 0,
        needed: 100,
        percent: 0
      },
      currentStreak: 0,
      totalQuestionsAnswered: 0,
      masteredCount: 0
    };
    const level = childData.stats?.level || 1;
    const xp = childData.stats?.xp || 0;
    const progress = getProgressToNextLevel(xp);
    const currentStreak = childData.dailyStreak?.currentWeekDays || 0;
    const totalQuestionsAnswered = Object.keys(childData.questionStates || {}).length;
    const masteredCount = growthData ? growthData.modules.filter(m => m.mastery >= 85).length : 0;
    return {
      level,
      xp,
      progress,
      currentStreak,
      totalQuestionsAnswered,
      masteredCount
    };
  }, [childData, growthData]);
  const handleExportCsv = () => {
    if (!selectedChild || !growthData) return;
    const headers = ['Vak', 'Beheersing (%)', 'Niveau', 'Trend', 'Trend (%)'];
    const rows = growthData.modules.map(m => {
      const trendText = m.trend === 'up' ? 'Stijgt' : m.trend === 'down' ? 'Daalt' : 'Stabiel';
      return [`"${m.module.title}"`, m.mastery, `"${getMasteryLabel(m.mastery).label}"`, `"${trendText}"`, m.delta].join(',');
    });
    const csvContent = [headers.join(','), ...rows].join('\n');
    const safeName = selectedChild.displayName.replace(/[^a-zA-Z0-9]/g, '_');
    downloadCsv(csvContent, `voortgang_${safeName}`);
  };
  if (!selectedChild || !growthData) {
    return <PageLayout title="Voortgang" description="Bekijk de voortgang" data-id="element-6121">
        <div className="text-center py-20 text-slate-500 font-medium" data-id="element-6122">
          Geen data beschikbaar.
        </div>
      </PageLayout>;
  }
  const overallBenchmark = getBenchmarkContext(growthData.overallMastery, groupNum);
  return <PageLayout title="Voortgang" description={`Bekijk de voortgang van ${selectedChild.displayName}`} data-id="element-6123">
      <div className="max-w-5xl mx-auto space-y-8 pb-12" data-id="element-6124">
        <ChildSelectorBar linkedLearners={linkedLearners} selectedChildId={selectedChild.id} onSelect={setViewingLearner} data-id="element-6125" />

        {/* Export CSV */}
        <div className="flex justify-end" data-id="element-6126">
          <button onClick={handleExportCsv} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-bold text-emerald-700 hover:bg-emerald-50 transition-all border border-emerald-200 bg-white" title="Exporteer als CSV" data-id="element-6127">
            <Download className="w-4 h-4" data-id="element-6128" />
            <span className="hidden sm:inline" data-id="element-6129">Exporteer CSV</span>
          </button>
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-2 border-b border-slate-200" data-id="element-6130">
          <button onClick={() => setActiveTab('per-vak')} className={`px-4 py-3 text-sm font-bold transition-all relative ${activeTab === 'per-vak' ? 'text-emerald-700' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-6131">
            Per vak
            {activeTab === 'per-vak' && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-600" transition={{
            type: 'spring',
            stiffness: 500,
            damping: 30
          }} data-id="element-6132" />}
          </button>
          <button onClick={() => setActiveTab('prestaties')} className={`px-4 py-3 text-sm font-bold transition-all relative ${activeTab === 'prestaties' ? 'text-emerald-700' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-6133">
            Prestaties
            {activeTab === 'prestaties' && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-600" transition={{
            type: 'spring',
            stiffness: 500,
            damping: 30
          }} data-id="element-6134" />}
          </button>
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait" data-id="element-6135">
          {activeTab === 'per-vak' && <motion.div key="per-vak" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} className="space-y-8" data-id="element-6136">
              {/* Growth Story */}
              <div className="flex flex-col md:flex-row items-center gap-6 bg-emerald-50 p-6 rounded-3xl border border-emerald-100 shadow-sm" data-id="element-6137">
                <div className="w-24 h-24 bg-white rounded-2xl flex items-center justify-center border border-emerald-200 shadow-sm flex-shrink-0" data-id="element-6138">
                  <DjayAvatar size="lg" emotion="proud" data-id="element-6139" />
                </div>
                <div className="flex-1 text-center md:text-left" data-id="element-6140">
                  <h2 className="text-2xl font-black text-emerald-900 mb-2" data-id="element-6141">
                    Groei-overzicht
                  </h2>
                  <p className="text-emerald-800 font-medium leading-relaxed" data-id="element-6142">
                    {selectedChild.displayName} heeft een gemiddelde beheersing
                    van{' '}
                    <span className="font-black text-emerald-600" data-id="element-6143">
                      {growthData.overallMastery}%
                    </span>{' '}
                    <span className={`font-bold ${getMasteryLabel(growthData.overallMastery).colorClass}`} data-id="element-6144">
                      ({getMasteryLabel(growthData.overallMastery).label})
                    </span>{' '}
                    over alle geoefende vakken.
                    {growthData.strengths.length > 0 && ` Vooral ${growthData.strengths[0].module.title.toLowerCase()} gaat erg goed!`}
                  </p>
                  <div className={`inline-flex items-center gap-2 mt-3 px-3 py-1.5 rounded-lg border text-sm font-bold ${overallBenchmark.bgClass} ${overallBenchmark.colorClass}`} data-id="element-6145">
                    <span data-id="element-6146">{overallBenchmark.emoji}</span>
                    <span data-id="element-6147">{overallBenchmark.description}</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-6148">
                {/* Strengths */}
                <section data-id="element-6149">
                  <h3 className="text-lg font-black text-slate-900 flex items-center gap-2 mb-4" data-id="element-6150">
                    <Star className="w-5 h-5 text-amber-500" data-id="element-6151" />
                    Sterke punten
                  </h3>
                  <div className="space-y-3" data-id="element-6152">
                    {growthData.strengths.map((s, i) => {
                  const IconComp = getModuleIcon(s.module.icon);
                  return <motion.div key={s.module.id} initial={{
                    opacity: 0,
                    x: -20
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} transition={{
                    delay: i * 0.1
                  }} data-id="element-6153">
                          <Card className="p-4 border-emerald-100 bg-white shadow-sm flex items-center gap-4" data-id="element-6154">
                            <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0" data-id="element-6155">
                              <IconComp className="w-6 h-6" data-id="element-6156" />
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-6157">
                              <h4 className="font-bold text-slate-900 truncate" data-id="element-6158">
                                {s.module.title}
                              </h4>
                              <div className="text-xs font-medium text-slate-500 truncate" data-id="element-6159">
                                {s.module.description}
                              </div>
                            </div>
                            <div className="flex-shrink-0" data-id="element-6160">
                              <MasteryRing value={s.mastery} size={48} color="emerald" data-id="element-6161" />
                            </div>
                          </Card>
                        </motion.div>;
                })}
                    {growthData.strengths.length === 0 && <EmptyState title="Nog geen sterke punten" message="Nog niet genoeg geoefend om sterke punten te bepalen." emotion="encouraging" colorScheme="emerald" data-id="element-6162" />}
                  </div>
                </section>

                {/* Areas for Growth */}
                <section data-id="element-6163">
                  <h3 className="text-lg font-black text-slate-900 flex items-center gap-2 mb-4" data-id="element-6164">
                    <TrendingUp className="w-5 h-5 text-indigo-500" data-id="element-6165" />
                    Hier kan {selectedChild.displayName} nog groeien
                  </h3>
                  <div className="space-y-3" data-id="element-6166">
                    {growthData.areasForGrowth.map((w, i) => {
                  const IconComp = getModuleIcon(w.module.icon);
                  return <motion.div key={w.module.id} initial={{
                    opacity: 0,
                    x: 20
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} transition={{
                    delay: i * 0.1
                  }} data-id="element-6167">
                          <Card className="p-4 border-indigo-100 bg-white shadow-sm flex items-center gap-4" data-id="element-6168">
                            <div className="w-12 h-12 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0" data-id="element-6169">
                              <IconComp className="w-6 h-6" data-id="element-6170" />
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-6171">
                              <h4 className="font-bold text-slate-900 truncate" data-id="element-6172">
                                {w.module.title}
                              </h4>
                              <div className="text-xs font-medium text-slate-500 truncate" data-id="element-6173">
                                Oefening baart kunst!
                              </div>
                            </div>
                            <div className="flex-shrink-0" data-id="element-6174">
                              <MasteryRing value={w.mastery} size={48} color="indigo" data-id="element-6175" />
                            </div>
                          </Card>
                        </motion.div>;
                })}
                    {growthData.areasForGrowth.length === 0 && <EmptyState title="Geen aandachtspunten" message="Geen specifieke aandachtspunten gevonden. Goed bezig!" emotion="celebrating" colorScheme="emerald" data-id="element-6176" />}
                  </div>
                </section>
              </div>

              {/* Latest Assessment Result */}
              {latestAssessmentReport && <section className="pt-4" data-id="element-6177">
                  <h3 className="text-lg font-black text-slate-900 mb-4" data-id="element-6178">
                    Laatste toetsresultaat
                  </h3>
                  <Card className="p-6 border-slate-200 shadow-sm bg-white flex flex-col sm:flex-row sm:items-center justify-between gap-6" data-id="element-6179">
                    <div data-id="element-6180">
                      <div className="flex items-center gap-2 mb-2" data-id="element-6181">
                        <span className="text-sm font-bold text-slate-500" data-id="element-6182">
                          {new Date(latestAssessmentReport.completedAt).toLocaleDateString('nl-NL', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                        </span>
                        <span className="text-xs font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded" data-id="element-6183">
                          Groep {latestAssessmentReport.selectedGroup}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 font-medium max-w-md" data-id="element-6184">
                        Dit resultaat geeft een indicatie van het huidige niveau
                        op basis van de Meester djay taxonomie.
                        <span className="block mt-1 text-xs text-slate-400 italic" data-id="element-6185">
                          Let op: dit is indicatief en geen officiële
                          CITO-toets.
                        </span>
                      </p>
                    </div>

                    <div className="flex items-center gap-4 shrink-0" data-id="element-6186">
                      <div className="text-right" data-id="element-6187">
                        <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1" data-id="element-6188">
                          Niveau
                        </div>
                        <div className="text-xl font-black text-slate-800" data-id="element-6189">
                          {latestAssessmentReport.overallEstimatedLevelCode}
                        </div>
                      </div>

                      {(() => {
                  const cito = levelCodeToCitoLevel(latestAssessmentReport.overallEstimatedLevelCode, latestAssessmentReport.selectedGroup);
                  if (!cito) return null;
                  return <div className={`flex flex-col items-center justify-center px-4 py-3 rounded-xl border-2 ${cito.bgColor} ${cito.borderColor}`} data-id="element-6190">
                            <span className={`text-2xl font-black ${cito.color}`} data-id="element-6191">
                              {cito.label}
                            </span>
                          </div>;
                })()}
                    </div>
                  </Card>
                </section>}

              {/* All Modules Detail */}
              <section className="pt-4" data-id="element-6192">
                <h3 className="text-lg font-black text-slate-900 mb-4" data-id="element-6193">
                  Alle geoefende vakken
                </h3>
                <Card className="border-slate-200 shadow-sm bg-white overflow-hidden" data-id="element-6194">
                  <div className="divide-y divide-slate-100" data-id="element-6195">
                    {growthData.modules.map(m => {
                  const IconComp = getModuleIcon(m.module.icon);
                  const isGood = m.mastery >= 60;
                  const isWarning = m.mastery < 40;
                  const barColor = isGood ? 'bg-emerald-500' : isWarning ? 'bg-rose-500' : 'bg-amber-400';
                  const moduleBenchmark = getBenchmarkContext(m.mastery, groupNum);
                  return <div key={m.module.id} className="p-5 hover:bg-slate-50 transition-colors" data-id="element-6196">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6" data-id="element-6197">
                            <div className="flex items-center gap-4 sm:w-1/3" data-id="element-6198">
                              <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500 flex-shrink-0" data-id="element-6199">
                                <IconComp className="w-5 h-5" data-id="element-6200" />
                              </div>
                              <div data-id="element-6201">
                                <div className="font-bold text-slate-900" data-id="element-6202">
                                  {m.module.title}
                                </div>
                                <div className="flex items-center gap-2" data-id="element-6203">
                                  <span className="text-xs font-medium text-slate-500" data-id="element-6204">
                                    {getMasteryLabel(m.mastery).label}
                                  </span>
                                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${moduleBenchmark.colorClass} ${moduleBenchmark.bgClass}`} data-id="element-6205">
                                    {moduleBenchmark.label}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="flex-1 flex items-center gap-4" data-id="element-6206">
                              <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden" data-id="element-6207">
                                <motion.div initial={{
                            width: 0
                          }} animate={{
                            width: `${m.mastery}%`
                          }} transition={{
                            duration: 1,
                            ease: 'easeOut'
                          }} className={`h-full rounded-full ${barColor}`} data-id="element-6208" />
                              </div>
                              <div className="w-12 text-right text-xl font-black text-slate-900" data-id="element-6209">
                                {m.mastery}%
                              </div>
                              <div className="w-24 flex justify-end" data-id="element-6210">
                                {m.trend === 'up' && <span className="flex items-center gap-1 text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md uppercase tracking-wider" data-id="element-6211">
                                    <ArrowUpRight className="w-3 h-3" data-id="element-6212" />{' '}
                                    {m.delta > 0 ? `+${m.delta}%` : 'Stijgt'}
                                  </span>}
                                {m.trend === 'down' && <span className="flex items-center gap-1 text-xs font-bold text-rose-600 bg-rose-50 px-2 py-1 rounded-md uppercase tracking-wider" data-id="element-6213">
                                    <ArrowDownRight className="w-3 h-3" data-id="element-6214" />{' '}
                                    {m.delta < 0 ? `${m.delta}%` : 'Daalt'}
                                  </span>}
                                {m.trend === 'stable' && <span className="flex items-center gap-1 text-xs font-bold text-slate-500 bg-slate-100 px-2 py-1 rounded-md uppercase tracking-wider" data-id="element-6215">
                                    <ArrowRight className="w-3 h-3" data-id="element-6216" /> Stabiel
                                  </span>}
                              </div>
                            </div>
                          </div>
                        </div>;
                })}
                  </div>
                </Card>
              </section>
            </motion.div>}

          {activeTab === 'prestaties' && <motion.div key="prestaties" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} className="space-y-8" data-id="element-6217">
              {/* Level Progress Card */}
              <Card className="p-6 bg-white border-slate-200 shadow-sm overflow-hidden relative" data-id="element-6218">
                <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-50 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none opacity-60" data-id="element-6219" />

                <div className="relative z-10 flex flex-col md:flex-row items-center gap-8" data-id="element-6220">
                  <div className="w-32 h-32 bg-emerald-50 rounded-3xl flex items-center justify-center border-2 border-emerald-100 shadow-inner flex-shrink-0 relative" data-id="element-6221">
                    <div className="absolute -top-3 -right-3 w-10 h-10 bg-gradient-to-br from-amber-300 to-orange-500 rounded-full flex items-center justify-center shadow-lg border-2 border-white z-20" data-id="element-6222">
                      <span className="text-white font-black text-sm" data-id="element-6223">
                        {prestatiesData.level}
                      </span>
                    </div>
                    <DjayAvatar size="lg" emotion="proud" data-id="element-6224" />
                  </div>

                  <div className="flex-1 w-full space-y-4" data-id="element-6225">
                    <div data-id="element-6226">
                      <div className="flex items-center justify-between mb-1" data-id="element-6227">
                        <h3 className="text-xl font-black text-slate-800" data-id="element-6228">
                          {getLevelTitle(prestatiesData.level)}
                        </h3>
                        <span className="text-sm font-bold text-emerald-600" data-id="element-6229">
                          Level {prestatiesData.level}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 font-medium" data-id="element-6230">
                        Nog{' '}
                        {prestatiesData.progress.needed - prestatiesData.progress.current}{' '}
                        XP tot het volgende level
                      </p>
                    </div>

                    <div className="space-y-2" data-id="element-6231">
                      <div className="h-4 bg-slate-100 rounded-full overflow-hidden shadow-inner" data-id="element-6232">
                        <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full transition-all duration-1000 ease-out relative" style={{
                      width: `${Math.max(5, prestatiesData.progress.percent)}%`
                    }} data-id="element-6233">
                          <div className="absolute inset-0 bg-white/20 w-full h-full" style={{
                        backgroundImage: 'linear-gradient(45deg, rgba(255,255,255,0.15) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.15) 75%, transparent 75%, transparent)',
                        backgroundSize: '1rem 1rem'
                      }} data-id="element-6234" />
                        </div>
                      </div>
                      <div className="flex justify-between text-xs font-bold text-slate-400" data-id="element-6235">
                        <span data-id="element-6236">{prestatiesData.xp} XP</span>
                        <span data-id="element-6237">
                          {prestatiesData.xp + (prestatiesData.progress.needed - prestatiesData.progress.current)}{' '}
                          XP
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-6238">
                <Card className="p-4 bg-white border-slate-200 shadow-sm flex items-center gap-4" data-id="element-6239">
                  <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0" data-id="element-6240">
                    <Flame className="w-5 h-5 text-orange-500" data-id="element-6241" />
                  </div>
                  <div data-id="element-6242">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-6243">
                      Reeks
                    </div>
                    <div className="text-lg font-black text-slate-800" data-id="element-6244">
                      {prestatiesData.currentStreak} dagen
                    </div>
                  </div>
                </Card>

                <Card className="p-4 bg-white border-slate-200 shadow-sm flex items-center gap-4" data-id="element-6245">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0" data-id="element-6246">
                    <Star className="w-5 h-5 text-blue-500" data-id="element-6247" />
                  </div>
                  <div data-id="element-6248">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-6249">
                      Vragen
                    </div>
                    <div className="text-lg font-black text-slate-800" data-id="element-6250">
                      {prestatiesData.totalQuestionsAnswered}
                    </div>
                  </div>
                </Card>

                <Card className="p-4 bg-white border-slate-200 shadow-sm flex items-center gap-4" data-id="element-6251">
                  <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0" data-id="element-6252">
                    <Trophy className="w-5 h-5 text-purple-500" data-id="element-6253" />
                  </div>
                  <div data-id="element-6254">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-6255">
                      Meester
                    </div>
                    <div className="text-lg font-black text-slate-800" data-id="element-6256">
                      {prestatiesData.masteredCount} vakken
                    </div>
                  </div>
                </Card>

                <Card className="p-4 bg-white border-slate-200 shadow-sm flex items-center gap-4" data-id="element-6257">
                  <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0" data-id="element-6258">
                    <Sparkles className="w-5 h-5 text-amber-500" data-id="element-6259" />
                  </div>
                  <div data-id="element-6260">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-6261">
                      Totaal XP
                    </div>
                    <div className="text-lg font-black text-slate-800" data-id="element-6262">
                      {prestatiesData.xp}
                    </div>
                  </div>
                </Card>
              </div>

              {/* Achievements Section */}
              <div className="pt-4" data-id="element-6263">
                <AchievementsSection state={childData!} totalQuestionsAnswered={prestatiesData.totalQuestionsAnswered} masteredCount={prestatiesData.masteredCount} currentStreak={prestatiesData.currentStreak} data-id="element-6264" />
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </PageLayout>;
}